/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import Modal from "../Shared/Modal";
import ConfirmedImg from "../../assets/ConfirmedImg.svg";
import Button from "../Shared/Button";
import Input from "../Shared/Input";
import TextArea from "../Shared/TextArea";
import * as Yup from "yup";
import { useFormik } from "formik";
import { ButtonTypes } from "../../utils/consts";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { queryClient } from "../../main";
import Selectbox from "../Shared/SelectBox";
import { CreateTraining } from "../../api/Training/CreateTraining";
import { option } from "../Shared/CountriesSelectbox";

const industryOptions: option[] = [
    { id: 'it', name: 'Information Technology' },
    { id: 'manufacturing', name: 'Manufacturing' },
    { id: 'retail', name: 'Retail' },
    { id: 'financial', name: 'Financial Services' },
    { id: 'healthcare', name: 'Healthcare' },
    { id: 'education', name: 'Education' },
    { id: 'construction', name: 'Construction' },
    { id: 'agriculture', name: 'Agriculture' },
    { id: 'energy', name: 'Energy and Utilities' },
    { id: 'transportation', name: 'Transportation and Logistics' },
    { id: 'hospitality', name: 'Hospitality and Tourism' },
    { id: 'real_estate', name: 'Real Estate' },
    { id: 'media', name: 'Media and Entertainment' },
    { id: 'telecommunications', name: 'Telecommunications' },
    { id: 'legal', name: 'Legal Services' },
    { id: 'non_profit', name: 'Non-Profit Organizations' },
    { id: 'government', name: 'Government and Public Administration' },
    { id: 'pharma', name: 'Pharmaceuticals and Biotechnology' },
    { id: 'automotive', name: 'Automotive' },
    { id: 'aerospace', name: 'Aerospace and Defense' },
    { id: 'food', name: 'Food and Beverage' },
    { id: 'mining', name: 'Mining and Metals' },
    { id: 'chemicals', name: 'Chemical Industry' },
    { id: 'textile', name: 'Textile and Apparel' },
    { id: 'environmental', name: 'Environmental Services' },
    { id: 'consulting', name: 'Consulting Services' },
    { id: 'insurance', name: 'Insurance' },
    { id: 'arts', name: 'Arts and Culture' },
    { id: 'sports', name: 'Sports and Recreation' },
    { id: 'personal', name: 'Personal Services' },
    { id: 'other', name: 'Other' }
]
const trainingAreaOptions: option[] = [
    { id: 'leadership', name: 'Leadership Development' },
    { id: 'technical', name: 'Technical Skills' },
    { id: 'sales', name: 'Sales Training' },
    { id: 'marketing', name: 'Marketing' },
    { id: 'soft_skills', name: 'Soft Skills' },
    { id: 'customer_service', name: 'Customer Service' },
    { id: 'compliance', name: 'Compliance and Regulatory' },
    { id: 'project_management', name: 'Project Management' },
    { id: 'it_training', name: 'Information Technology' },
    { id: 'hr', name: 'Human Resources' },
    { id: 'financial', name: 'Financial Management' },
    { id: 'health_safety', name: 'Health and Safety' },
    { id: 'diversity', name: 'Diversity and Inclusion' },
    { id: 'language', name: 'Language and Communication' },
    { id: 'time_management', name: 'Time Management' },
    { id: 'conflict_resolution', name: 'Conflict Resolution' },
    { id: 'emotional_intelligence', name: 'Emotional Intelligence' },
    { id: 'data_analysis', name: 'Data Analysis' },
    { id: 'change_management', name: 'Change Management' },
    { id: 'creative_thinking', name: 'Creative Thinking' },
    { id: 'negotiation', name: 'Negotiation Skills' },
    { id: 'supply_chain', name: 'Supply Chain Management' },
    { id: 'quality_assurance', name: 'Quality Assurance' },
    { id: 'risk_management', name: 'Risk Management' },
    { id: 'strategic_planning', name: 'Strategic Planning' },
    { id: 'cybersecurity', name: 'Cybersecurity' },
    { id: 'remote_work', name: 'Remote Work Skills' },
    { id: 'presentation_skills', name: 'Presentation Skills' },
    { id: 'coaching', name: 'Coaching and Mentoring' },
    { id: 'stress_management', name: 'Stress Management' },
    { id: 'other', name: 'Other' }
]
const budgetRangeOptions: option[] = [
    { id: 'less_50k', name: 'Less than 50,000' },
    { id: '50k_250k', name: '50,000 - 250,000' },
    { id: 'more_250k', name: 'More than 250,000' }
]
const employeeRangeOptions: option[] = [
    { id: '1-10', name: '1-10 employees' },
    { id: '11-50', name: '11-50 employees' },
    { id: '51-200', name: '51-200 employees' },
    { id: '201-500', name: '201-500 employees' },
    { id: '500+', name: 'More than 500 employees' }
]


type ModalProps = {
    isOpen: boolean;
    closeModal: Dispatch<SetStateAction<boolean>>;
    type: 'design' | 'tna';
}

const TrainingModal = ({ isOpen, closeModal, type }: ModalProps) => {
    const validationSchema = Yup.object({
        companyName: Yup.string().required("Company name is required"),
        industry: Yup.string().required("Industry is required"),
        ...(type === 'design' && {
            areaOfTraining: Yup.string().required("Training area is required"),
        }),
        numberOfEmployees: Yup.string().required("Number of employees is required"),
        expectedBudget: Yup.string(),
        contactPerson: Yup.string().required("Contact person name is required"),
        email: Yup.string()
            .email("Invalid email address")
            .required("Email is required"),
        phone: Yup.string()
            .required("Phone number is required")
            .matches(/^[0-9+\-\s()]+$/, "Invalid phone number")
            .min(10, "Phone number is too short")
            .max(15, "Phone number is too long"),
        ...(type === 'tna' && {
            isTrainingNeeded: Yup.boolean()
        }),
        additionalNote: Yup.string(),
    });

    const [step, setStep] = useState(1);
    useEffect(() => {
        formik.resetForm();

    }, [type, isOpen])

    const { mutate, error } = useMutation({
        mutationFn: CreateTraining,
        onSuccess: () => {
            setStep(2);
            queryClient.invalidateQueries({ queryKey: ["trainings"] });
        },
        onError: () => {
            formik.setSubmitting(false);
        },
    });

    const formik = useFormik({
        initialValues: {
            companyName: "",
            industry: "",
            areaOfTraining: undefined,
            numberOfEmployees: "",
            expectedBudget: "",
            contactPerson: "",
            email: "",
            phone: "",
            isTrainingNeeded: type === 'tna' ? true : undefined,
            additionalNote: "",
        },
        validationSchema,
        onSubmit: (values) => {
            //@ts-expect-error
            mutate(values);
        },
    });

    return (
        <Modal className={"!max-w-[671px] min-h-[750px]"} isOpen={isOpen} closeModal={closeModal}>
            <div className="flex flex-col items-center h-full ju px-7 py-12 md:py-16">
                <div className="max-w-lg w-full text-center h-full flex flex-col justify-between">
                    {step === 1 && (
                        <form className="h-full flex flex-col justify-between" onSubmit={formik.handleSubmit}>
                            <div className="text-start">
                                <p className="text-[#808080] tracking-[-0.02em] mb-8 md:text-xl">
                                    Please confirm your details before proceeding to finalize the session.
                                </p>
                                <div className="space-y-5">
                                    <Input
                                        value={formik.values.companyName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.companyName && formik.errors.companyName ? formik.errors.companyName
                                            : ""
                                        }
                                        name="companyName"
                                        title="Company Name"
                                        inputStyles="!h-12 !border-[#D0D5DD] hover:!border-secondary focus:!border-secondary !bg-transparent"
                                    />
                                    <Selectbox
                                        title="Industry"
                                        name="industry"
                                        titleStyles="!text-base"
                                        className=" !border-[#D0D5DD] hover:!border-secondary"
                                        setSelected={(option: option | null) => formik.setFieldValue('industry', option?.id)}
                                        selected={industryOptions.find(opt => opt.id === formik.values.industry)}
                                        selectOptions={industryOptions}
                                        error={formik.touched.industry && formik.errors.industry ? formik.errors.industry : ""}
                                    />
                                    {type === 'design' && (
                                        <Selectbox
                                            title="Training Area"
                                            name="areaOfTraining"
                                            titleStyles="!text-base"
                                            className=" !border-[#D0D5DD] hover:!border-secondary"
                                            setSelected={(option: option | null) => formik.setFieldValue('areaOfTraining', option?.id)}
                                            selected={trainingAreaOptions.find(opt => opt.id === formik.values.areaOfTraining)}
                                            selectOptions={trainingAreaOptions}
                                            error={formik.touched.areaOfTraining && formik.errors.areaOfTraining ? formik.errors.areaOfTraining : ""}
                                        />
                                    )}

                                    <Selectbox
                                        title="Number of Employees"
                                        name="numberOfEmployees"
                                        titleStyles="!text-base"
                                        className=" !border-[#D0D5DD] hover:!border-secondary"
                                        setSelected={(option: option | null) => formik.setFieldValue('numberOfEmployees', option?.id)}
                                        selected={employeeRangeOptions.find(opt => opt.id === formik.values.numberOfEmployees)}
                                        selectOptions={employeeRangeOptions}
                                        error={formik.touched.numberOfEmployees && formik.errors.numberOfEmployees ? formik.errors.numberOfEmployees : ""}
                                    />
                                    <Selectbox
                                        title="Expected Budget (Optional)"
                                        name="expectedBudget"
                                        titleStyles="!text-base"
                                        className=" !border-[#D0D5DD] hover:!border-secondary"
                                        setSelected={(option: option | null) => formik.setFieldValue('expectedBudget', option?.id)}
                                        selected={budgetRangeOptions.find(opt => opt.id === formik.values.expectedBudget)}
                                        selectOptions={budgetRangeOptions}
                                        error={formik.touched.expectedBudget && formik.errors.expectedBudget ? formik.errors.expectedBudget : ""}
                                    />
                                    <Input
                                        value={formik.values.contactPerson}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.contactPerson && formik.errors.contactPerson ? formik.errors.contactPerson
                                            : ""
                                        }
                                        name="contactPerson"
                                        title="Contact Person Name"
                                        inputStyles="!h-12 !border-[#D0D5DD] hover:!border-secondary focus:!border-secondary !bg-transparent"
                                    />
                                    <Input
                                        value={formik.values.email}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.email && formik.errors.email ? formik.errors.email
                                            : ""
                                        }
                                        name="email"
                                        title="Email Address"
                                        inputStyles="!h-12 !border-[#D0D5DD] hover:!border-secondary focus:!border-secondary !bg-transparent"
                                    />
                                    <Input
                                        value={formik.values.phone}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.phone && formik.errors.phone ? formik.errors.phone
                                            : ""
                                        }
                                        name="phone"
                                        title="Phone Number"
                                        inputStyles="!h-12 !border-[#D0D5DD] hover:!border-secondary focus:!border-secondary !bg-transparent"
                                    />
                                    <TextArea
                                        value={formik.values.additionalNote}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        name="additionalNote"
                                        title="Additional Notes (Optional)"
                                        inputStyles="!h-28 !border-[#D0D5DD] hover:!border-secondary focus:!border-secondary !bg-transparent"
                                        placeholder={""} />
                                    {type === 'tna' && (
                                        <div className="space-y-2 ">
                                            <label className="block text-sm font-medium text-gray-700">
                                                Do you require a Training Needs Analysis (TNA)?
                                            </label>
                                            <div className="flex gap-4 sm:flex-row flex-col">
                                                <label className="inline-flex items-center">
                                                    <input
                                                        type="radio"
                                                        name="isTrainingNeeded"
                                                        checked={formik.values.isTrainingNeeded === true}
                                                        onChange={() => formik.setFieldValue('isTrainingNeeded', true)}
                                                        className="form-radio h-4 w-4 text-blue-600"
                                                    />
                                                    <span className="ml-2">Yes, we need a TNA</span>
                                                </label>
                                                <label className="inline-flex items-center">
                                                    <input
                                                        type="radio"
                                                        name="isTrainingNeeded"
                                                        checked={formik.values.isTrainingNeeded === false}
                                                        onChange={() => formik.setFieldValue('isTrainingNeeded', false)}
                                                        className="form-radio h-4 w-4 text-blue-600"
                                                    />
                                                    <span className="ml-2">No, we know our needs</span>
                                                </label>
                                            </div>
                                        </div>
                                    )}

                                </div>
                            </div>
                            {error instanceof AxiosError && (
                                <span className="text-red-500 font-bold text-lg mt-4">
                                    {typeof error?.response?.data?.message === "string"
                                        ? error?.response?.data?.error
                                        : "An error occurred"}
                                </span>
                            )}

                            <Button disabled={formik.isSubmitting} type={ButtonTypes.SUBMIT} variant="primary" className="!bg-[#3D50F8] mt-10 hover:!bg-[#1a2159] !h-[42px] !text-base w-full">
                                Confirm Details
                            </Button>
                        </form>
                    )}

                    {step === 2 && (
                        <>
                            <h1 className="text-3xl font-semibold mb-2 tracking-[-0.02em] sm:text-4xl">
                                Training Confirmed
                            </h1>
                            <p className="text-[#808080] tracking-[-0.02em] mb-12 md:text-xl">
                                Your training has been confirmed. We will contact you soon.
                            </p>
                            <img src={ConfirmedImg} className="mx-auto mb-24" alt="Confirmed" />
                            <Button onClick={() => closeModal(false)} variant="primary" className="!bg-[#3D50F8] hover:!bg-[#1a2159] !h-[42px] !text-base w-full">
                                Done
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default TrainingModal;
